/**
 * Auth store for managing user account state.
 * NOT persisted - auth state is derived from httpOnly cookies via initAuth().
 */

import { create } from 'zustand'
import i18next from 'i18next'
import { UserRead } from '@/types/auth'
import { useUIStore } from './uiStore'
import { useWalletStore } from './walletStore'
import { clearStoredPairing } from '@/services/pairingSocket'

interface AuthStoreState {
    user: UserRead | null
    isInitialized: boolean
    isLoading: boolean
    error: string | null

    initAuth: () => Promise<void>
    login: (email: string, password: string) => Promise<void>
    register: (
        email: string,
        username: string,
        password: string,
        acceptTerms: boolean
    ) => Promise<void>
    logout: () => Promise<void>
    setUser: (user: UserRead) => void
    onAuthLost: () => void
    setError: (msg: string) => void
    clearError: () => void
}

export const selectIsAuthenticated = (s: AuthStoreState) => s.user !== null

export const useAuthStore = create<AuthStoreState>((set) => ({
    user: null,
    isInitialized: false,
    isLoading: false,
    error: null,

    initAuth: async () => {
        try {
            // Lazy import to avoid circular dependency with api.ts
            const { getMe } = await import('@/services/api')
            const user = await getMe()
            set({ user, isInitialized: true })
        } catch {
            set({ user: null, isInitialized: true })
        }
    },

    login: async (email, password) => {
        set({ isLoading: true, error: null })
        try {
            const { login } = await import('@/services/api')
            const user = await login(email, password)
            set({ user, isLoading: false })
        } catch (err) {
            const rawMsg = (err as { message?: string }).message
            const msg = rawMsg !== undefined && rawMsg !== '' ? rawMsg : i18next.t("auth.loginFailed")
            set({ error: msg, isLoading: false })
            throw err
        }
    },

    register: async (email, username, password, acceptTerms) => {
        set({ isLoading: true, error: null })
        try {
            const { register } = await import('@/services/api')
            const user = await register(email, username, password, acceptTerms)
            set({ user, isLoading: false })
        } catch (err) {
            const rawMsg = (err as { message?: string }).message
            const msg = rawMsg !== undefined && rawMsg !== '' ? rawMsg : i18next.t("auth.registerFailed")
            set({ error: msg, isLoading: false })
            throw err
        }
    },

    logout: async () => {
        set({ isLoading: true })
        try {
            const { logout } = await import('@/services/api')
            await logout()
        } catch {
            // Ignore errors - clear local state regardless
        } finally {
            set({ user: null, isLoading: false })
            // The balance belongs to the account that just left; keeping it would
            // show the previous user's coins to whoever logs in next.
            useWalletStore.getState().clear()
            clearStoredPairing()
        }
    },

    setUser: (user) => set({ user }),

    onAuthLost: () => {
        set({ user: null })
        useWalletStore.getState().clear()
        clearStoredPairing()
        useUIStore.getState().addToast({
            type: 'error',
            message: i18next.t("auth.sessionExpired"),
        })
    },

    setError: (msg) => set({ error: msg }),
    clearError: () => set({ error: null }),
}))
